import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, Lock, CheckCircle2, AlertTriangle, Eye, EyeOff } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [state, setState] = useState("loading"); // loading | valid | expired | error | success
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!token) { setState("error"); setErrorMsg("Lien invalide — aucun token fourni."); return; }
    fetch(`${API}/api/hub/auth/validate-invitation/${token}`)
      .then(r => r.json().then(d => ({ ok: r.ok, data: d })))
      .then(({ ok, data }) => {
        if (ok && data.valid) { setEmail(data.email); setState("valid"); }
        else { setState("expired"); setErrorMsg(data.detail || "Lien expiré ou invalide."); }
      })
      .catch(() => { setState("error"); setErrorMsg("Impossible de vérifier le lien."); });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { setErrorMsg("Minimum 8 caractères."); return; }
    if (password !== confirm) { setErrorMsg("Les mots de passe ne correspondent pas."); return; }
    setErrorMsg("");
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/hub/auth/set-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        if (data.session_token) localStorage.setItem("session_token", data.session_token);
        setState("success");
        setTimeout(() => navigate("/hub"), 2000);
      } else {
        setErrorMsg(data.detail || "Erreur lors de la création du mot de passe.");
      }
    } catch {
      setErrorMsg("Erreur réseau.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "var(--oh-bg, #0a0a0f)" }}>
      <div className="w-full max-w-md space-y-6">
        {/* Logo */}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white">OmniHub IA</h1>
          <p className="text-sm text-zinc-500 mt-1">Activation de votre compte</p>
        </div>

        {/* Card */}
        <div className="rounded-xl p-6 space-y-5" style={{ background: "var(--oh-bg-surface, #111118)", border: "1px solid var(--oh-border, #1e1e2e)" }}>

          {state === "loading" && (
            <div className="flex flex-col items-center gap-3 py-8">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
              <p className="text-sm text-zinc-400">Vérification du lien...</p>
            </div>
          )}

          {state === "expired" && (
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <AlertTriangle className="w-10 h-10 text-amber-400" />
              <p className="text-white font-medium">Lien expiré ou invalide</p>
              <p className="text-sm text-zinc-400">{errorMsg}</p>
              <p className="text-xs text-zinc-500 mt-2">Contactez votre administrateur pour recevoir une nouvelle invitation.</p>
            </div>
          )}

          {state === "error" && (
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <AlertTriangle className="w-10 h-10 text-red-400" />
              <p className="text-white font-medium">Erreur</p>
              <p className="text-sm text-zinc-400">{errorMsg}</p>
            </div>
          )}

          {state === "success" && (
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <CheckCircle2 className="w-10 h-10 text-green-400" />
              <p className="text-white font-medium">Compte activé</p>
              <p className="text-sm text-zinc-400">Redirection vers OmniHub...</p>
            </div>
          )}

          {state === "valid" && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="text-center mb-2">
                <Lock className="w-8 h-8 text-cyan-400 mx-auto mb-2" />
                <p className="text-sm text-zinc-400">Créez votre mot de passe pour</p>
                <p className="text-white font-medium" data-testid="invite-email">{email}</p>
              </div>

              <div>
                <label className="text-xs text-zinc-400 mb-1.5 block">Mot de passe</label>
                <div className="relative">
                  <Input
                    data-testid="set-password-input"
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Minimum 8 caractères"
                    className="bg-zinc-800 border-zinc-700 text-white pr-10"
                    required
                    minLength={8}
                    autoFocus
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs text-zinc-400 mb-1.5 block">Confirmer le mot de passe</label>
                <Input
                  data-testid="set-password-confirm"
                  type="password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  placeholder="Retapez le mot de passe"
                  className="bg-zinc-800 border-zinc-700 text-white"
                  required
                  minLength={8}
                />
              </div>

              {errorMsg && (
                <p className="text-xs text-red-400" data-testid="set-password-error">{errorMsg}</p>
              )}

              <Button
                type="submit"
                disabled={saving || password.length < 8 || password !== confirm}
                className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
                data-testid="set-password-submit"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Activer mon compte
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
